import { Connection, PublicKey, Transaction, TransactionInstruction } from '@solana/web3.js';
import { getCurrentRpcEndpoint, SOLANA_CONFIG } from '../config/security';
import { secureStorage, sanitizeErrorMessage } from '../utils/security';

/**
 * Comprehensive Reputation System Service
 * Manages user reputation, token staking, and DAO governance
 */
export class ReputationService {
  constructor(connection, wallet) {
    this.connection = connection || new Connection(getCurrentRpcEndpoint());
    this.wallet = wallet;
    this.znoozinuTokenAddress = new PublicKey('5cLKdANQXzUPMXgpba19Zwprhmi8yQW6R5D66oTQZ9ZN');
    
    // Initialize in-memory cache to reduce RPC calls
    this.cache = new Map();
    this.cacheTimeout = 300000; // 5 minutes
    
    // Rate limiting configuration
    this.rpcCallCount = 0;
    this.rpcCallLimit = 50; // Max RPC calls per minute
    this.rpcResetTime = Date.now() + 60000;
    
    // Reputation scoring constants
    this.REPUTATION_WEIGHTS = {
      POST_UPVOTE: 5,
      POST_DOWNVOTE: -3,
      COMMENT_UPVOTE: 2,
      COMMENT_DOWNVOTE: -1,
      CONTENT_FLAGGED: -10,
      CONTENT_APPROVED: 8,
      DAO_PARTICIPATION: 3,
      PROOF_OF_HUMANITY: 50,
      STAKE_BONUS: 0.1, // Per 1000 tokens staked
      TIME_DECAY: 0.99 // Daily reputation decay
    };

    // Staking requirements
    this.STAKING_REQUIREMENTS = {
      POST: 100, // 100 ZNOOZINU tokens
      COMMENT: 50,
      LIKE: 10,
      SHARE: 25,
      FLAG_CONTENT: 200,
      DAO_VOTE: 500
    };

    // Reputation tiers and benefits
    this.REPUTATION_TIERS = {
      NEWCOMER: { min: 0, max: 99, dailyPosts: 3, dailyComments: 10 },
      TRUSTED: { min: 100, max: 499, dailyPosts: 10, dailyComments: 30 },
      VETERAN: { min: 500, max: 999, dailyPosts: 25, dailyComments: 75 },
      GUARDIAN: { min: 1000, max: 2499, dailyPosts: 50, dailyComments: 150 },
      ELDER: { min: 2500, max: Infinity, dailyPosts: 100, dailyComments: 300 }
    };
  }

  /**
   * Get user's current reputation score
   */
  async getUserReputation(walletAddress) {
    try {
      const cacheKey = `reputation_${walletAddress}`;
      const cached = secureStorage.getItem(cacheKey);
      
      if (cached && Date.now() - cached.timestamp < 300000) { // 5 minute cache
        return cached.reputation;
      }

      // Calculate reputation from blockchain data
      const reputation = await this.calculateReputationFromBlockchain(walletAddress);
      
      // Apply time decay
      const decayedReputation = this.applyTimeDecay(reputation);
      
      // Cache result
      secureStorage.setItem(cacheKey, {
        reputation: decayedReputation,
        timestamp: Date.now()
      });

      return decayedReputation;
    } catch (error) {
      console.error('Error getting user reputation:', sanitizeErrorMessage(error));
      return { score: 0, tier: 'NEWCOMER', error: true };
    }
  }

  /**
   * Calculate reputation from blockchain transactions and votes (optimized for RPC efficiency)
   */
  async calculateReputationFromBlockchain(walletAddress) {
    try {
      // Check rate limiting
      if (!this.canMakeRpcCall()) {
        console.warn('⚠️ RPC rate limit reached, using cached/default reputation');
        return this.getDefaultReputation();
      }

      let totalScore = 0;
      const activities = [];

      // Significantly reduce RPC calls by limiting transaction history
      const signatures = await this.connection.getSignaturesForAddress(
        new PublicKey(walletAddress),
        { limit: 50 } // Reduced from 1000 to 50 to prevent rate limiting
      );
      this.incrementRpcCall();

      // Process only recent transactions to avoid excessive RPC calls
      const maxTransactions = Math.min(signatures.length, 10); // Process max 10 transactions
      
      for (let i = 0; i < maxTransactions; i++) {
        if (!this.canMakeRpcCall()) {
          console.warn('⚠️ RPC rate limit reached during transaction processing');
          break;
        }

        try {
          const tx = await this.connection.getTransaction(signatures[i].signature, {
            commitment: 'confirmed'
          });
          this.incrementRpcCall();

          if (tx?.meta?.logMessages) {
            const reputationEvents = this.parseReputationEvents(tx.meta.logMessages);
            activities.push(...reputationEvents);
          }
        } catch (error) {
          // Skip failed transactions
          continue;
        }
      }

      // Calculate score from activities
      for (const activity of activities) {
        totalScore += this.REPUTATION_WEIGHTS[activity.type] || 0;
      }

      // Get staking bonus with fallback to avoid additional RPC calls
      let stakedAmount = 0;
      let stakingBonus = 0;
      
      try {
        if (this.canMakeRpcCall()) {
          stakedAmount = await this.getUserStakedAmount(walletAddress);
          stakingBonus = Math.floor(stakedAmount / 1000) * this.REPUTATION_WEIGHTS.STAKE_BONUS;
        }
      } catch (error) {
        console.warn('⚠️ Could not fetch staked amount, using default');
      }

      totalScore += stakingBonus;

      // Determine tier
      const tier = this.getReputationTier(totalScore);

      return {
        score: Math.max(0, totalScore),
        tier: tier.name,
        tierInfo: tier,
        activities: activities.length,
        stakedAmount,
        stakingBonus,
        lastUpdated: Date.now(),
        limitedScan: maxTransactions < signatures.length
      };
    } catch (error) {
      console.error('Error calculating reputation:', sanitizeErrorMessage(error));
      return this.getDefaultReputation();
    }
  }

  /**
   * Parse reputation events from transaction logs
   */
  parseReputationEvents(logMessages) {
    const events = [];
    
    for (const log of logMessages) {
      // Parse different types of reputation events
      if (log.includes('ChirpX Vote:')) {
        const voteData = this.extractJSONFromLog(log, 'ChirpX Vote:');
        if (voteData) {
          events.push({
            type: voteData.isUpvote ? 'POST_UPVOTE' : 'POST_DOWNVOTE',
            timestamp: voteData.timestamp,
            target: voteData.postId
          });
        }
      } else if (log.includes('ChirpX Flag:')) {
        const flagData = this.extractJSONFromLog(log, 'ChirpX Flag:');
        if (flagData) {
          events.push({
            type: 'CONTENT_FLAGGED',
            timestamp: flagData.timestamp,
            reason: flagData.reason
          });
        }
      } else if (log.includes('ChirpX DAO Vote:')) {
        const daoData = this.extractJSONFromLog(log, 'ChirpX DAO Vote:');
        if (daoData) {
          events.push({
            type: 'DAO_PARTICIPATION',
            timestamp: daoData.timestamp,
            proposal: daoData.proposalId
          });
        }
      } else if (log.includes('ChirpX Humanity:')) {
        const humanityData = this.extractJSONFromLog(log, 'ChirpX Humanity:');
        if (humanityData) {
          events.push({
            type: 'PROOF_OF_HUMANITY',
            timestamp: humanityData.timestamp,
            verified: humanityData.verified
          });
        }
      }
    }

    return events;
  }

  /**
   * Extract JSON data from transaction log
   */
  extractJSONFromLog(log, prefix) {
    try {
      const match = log.match(new RegExp(`${prefix}\\s*(.+)`));
      if (match && match[1]) {
        return JSON.parse(match[1]);
      }
    } catch (error) {
      // Invalid JSON, skip
    }
    return null;
  }

  /**
   * Get reputation tier based on score
   */
  getReputationTier(score) {
    for (const [tierName, tierInfo] of Object.entries(this.REPUTATION_TIERS)) {
      if (score >= tierInfo.min && score <= tierInfo.max) {
        return { name: tierName, ...tierInfo };
      }
    }
    return { name: 'NEWCOMER', ...this.REPUTATION_TIERS.NEWCOMER };
  }

  /**
   * Apply time decay to reputation score
   */
  applyTimeDecay(reputation) {
    if (!reputation.lastUpdated) return reputation;
    
    const daysSinceUpdate = (Date.now() - reputation.lastUpdated) / (1000 * 60 * 60 * 24);
    const decayFactor = Math.pow(this.REPUTATION_WEIGHTS.TIME_DECAY, daysSinceUpdate);
    
    return {
      ...reputation,
      score: Math.floor(reputation.score * decayFactor)
    };
  }

  /**
   * Get user's staked token amount
   */
  async getUserStakedAmount(walletAddress) {
    try {
      // Get user's token account for ZNOOZINU
      const tokenAccounts = await this.connection.getTokenAccountsByOwner(
        new PublicKey(walletAddress),
        { mint: this.znoozinuTokenAddress }
      );

      let totalStaked = 0;
      for (const account of tokenAccounts.value) {
        const accountInfo = await this.connection.getTokenAccountBalance(account.pubkey);
        // Check if this is a staking account (would need additional logic)
        // For now, assume all tokens in dedicated staking accounts are staked
        totalStaked += parseInt(accountInfo.value.amount) / Math.pow(10, accountInfo.value.decimals);
      }

      return totalStaked;
    } catch (error) {
      console.error('Error getting staked amount:', sanitizeErrorMessage(error));
      return 0;
    }
  }

  /**
   * Get posting stake requirement based on user's reputation (optimized to prevent RPC rate limits)
   */
  async getPostingStakeRequirement(walletAddress) {
    try {
      // First check cache to avoid excessive RPC calls
      const cacheKey = `posting_stake_${walletAddress}`;
      const cached = this.cache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < this.cacheTimeout) {
        console.log('🎯 Using cached posting stake requirement');
        return cached.data;
      }

      // Check if we can make RPC calls
      if (!this.canMakeRpcCall()) {
        console.warn('⚠️ RPC rate limit reached, using default stake requirement');
        return this.getDefaultStakeRequirement();
      }

      // Try to get reputation with aggressive timeout and fallback
      let reputation;
      try {
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Reputation lookup timeout')), 3000) // Reduced to 3s
        );
        reputation = await Promise.race([
          this.getUserReputation(walletAddress),
          timeoutPromise
        ]);
      } catch (error) {
        console.warn('⚠️ Reputation lookup failed, using default tier:', error.message);
        return this.getDefaultStakeRequirement();
      }
      
      const baseStake = this.STAKING_REQUIREMENTS.POST || 100;
      
      // Reduce stake requirement for higher reputation tiers
      const tierMultiplier = {
        'NEWCOMER': 1.0,
        'TRUSTED': 0.8,
        'VETERAN': 0.6,
        'GUARDIAN': 0.4,
        'ELDER': 0.2
      };
      
      const multiplier = tierMultiplier[reputation.tier] || 1.0;
      const requiredStake = Math.ceil(baseStake * multiplier);
      
      const result = {
        required: requiredStake,
        tier: reputation.tier,
        multiplier: multiplier,
        cached: false
      };

      // Cache the result
      this.cache.set(cacheKey, {
        data: result,
        timestamp: Date.now()
      });
      
      return result;
    } catch (error) {
      console.error('Error getting posting stake requirement:', error);
      return this.getDefaultStakeRequirement();
    }
  }

  /**
   * Check if user can afford to perform an action (stake requirement)
   */
  async canAffordAction(walletAddress, action, stakeAmount = null) {
    try {
      const stakedAmount = await this.getUserStakedAmount(walletAddress);
      const requiredStake = stakeAmount || this.STAKING_REQUIREMENTS[action.toUpperCase()] || 0;
      
      return {
        canAfford: stakedAmount >= requiredStake,
        required: requiredStake,
        current: stakedAmount,
        shortfall: Math.max(0, requiredStake - stakedAmount)
      };
    } catch (error) {
      console.error('Error checking if user can afford action:', error);
      return {
        canAfford: false,
        required: this.STAKING_REQUIREMENTS[action.toUpperCase()] || 10,
        current: 0,
        shortfall: this.STAKING_REQUIREMENTS[action.toUpperCase()] || 10
      };
    }
  }

  /**
   * Check if user can perform an action based on reputation and staking
   */
  async canPerformAction(walletAddress, action) {
    try {
      const reputation = await this.getUserReputation(walletAddress);
      const stakedAmount = await this.getUserStakedAmount(walletAddress);
      const requiredStake = this.STAKING_REQUIREMENTS[action.toUpperCase()] || 0;

      // Check staking requirement
      if (stakedAmount < requiredStake) {
        return {
          allowed: false,
          reason: 'INSUFFICIENT_STAKE',
          required: requiredStake,
          current: stakedAmount
        };
      }

      // Check reputation-based rate limits
      const tierLimits = reputation.tierInfo;
      const todayActions = await this.getTodayActionCount(walletAddress, action);

      let dailyLimit = 0;
      switch (action.toLowerCase()) {
        case 'post':
          dailyLimit = tierLimits.dailyPosts;
          break;
        case 'comment':
          dailyLimit = tierLimits.dailyComments;
          break;
        default:
          dailyLimit = Infinity; // No limit for other actions
      }

      if (todayActions >= dailyLimit) {
        return {
          allowed: false,
          reason: 'DAILY_LIMIT_EXCEEDED',
          limit: dailyLimit,
          current: todayActions
        };
      }

      return {
        allowed: true,
        reputation: reputation.score,
        tier: reputation.tier,
        remainingActions: dailyLimit - todayActions
      };
    } catch (error) {
      console.error('Error checking action permission:', sanitizeErrorMessage(error));
      return { allowed: false, reason: 'ERROR', error: true };
    }
  }

  /**
   * Get today's action count for user
   */
  async getTodayActionCount(walletAddress, action) {
    try {
      const today = new Date().toDateString();
      const cacheKey = `actions_${walletAddress}_${today}`;
      const cached = secureStorage.getItem(cacheKey);
      
      if (cached) {
        return cached[action] || 0;
      }

      // Count actions from blockchain (simplified - would need more complex logic)
      return 0;
    } catch (error) {
      return 0;
    }
  }

  /**
   * Record an action for rate limiting
   */
  async recordAction(walletAddress, action) {
    try {
      const today = new Date().toDateString();
      const cacheKey = `actions_${walletAddress}_${today}`;
      const cached = secureStorage.getItem(cacheKey) || {};
      
      cached[action] = (cached[action] || 0) + 1;
      secureStorage.setItem(cacheKey, cached);
      
      return true;
    } catch (error) {
      console.error('Error recording action:', sanitizeErrorMessage(error));
      return false;
    }
  }

  /**
   * Stake tokens for posting privileges
   */
  async stakeTokens(amount, duration = 30) { // Default 30 days
    try {
      if (!this.wallet) {
        throw new Error('Wallet not connected');
      }

      // Create staking transaction
      const transaction = new Transaction();
      
      // Add staking instruction (simplified - would need actual staking program)
      const stakeInstruction = new TransactionInstruction({
        keys: [
          { pubkey: this.wallet.publicKey, isSigner: true, isWritable: true }
        ],
        programId: new PublicKey(SOLANA_CONFIG.PROGRAM_IDS.CHIRPX),
        data: Buffer.from(JSON.stringify({
          action: 'stake',
          amount: amount,
          duration: duration,
          timestamp: Date.now()
        }))
      });

      transaction.add(stakeInstruction);

      // Get recent blockhash
      const { blockhash } = await this.connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = this.wallet.publicKey;

      // Sign and send transaction
      const signedTransaction = await this.wallet.signTransaction(transaction);
      const signature = await this.connection.sendRawTransaction(signedTransaction.serialize());
      
      // Wait for confirmation
      await this.connection.confirmTransaction(signature, 'confirmed');

      return {
        success: true,
        signature,
        amount,
        duration,
        explorerUrl: `https://explorer.solana.com/tx/${signature}?cluster=devnet`
      };
    } catch (error) {
      console.error('Error staking tokens:', sanitizeErrorMessage(error));
      return {
        success: false,
        error: sanitizeErrorMessage(error)
      };
    }
  }

  /**
   * Get user's reputation dashboard data
   */
  async getReputationDashboard(walletAddress) {
    try {
      const reputation = await this.getUserReputation(walletAddress);
      const stakedAmount = await this.getUserStakedAmount(walletAddress);
      const todayActions = await this.getTodayActionCount(walletAddress, 'post');
      
      return {
        reputation,
        stakedAmount,
        todayActions,
        availableActions: {
          posts: reputation.tierInfo.dailyPosts - todayActions,
          comments: reputation.tierInfo.dailyComments - (await this.getTodayActionCount(walletAddress, 'comment'))
        },
        stakingRequirements: this.STAKING_REQUIREMENTS,
        nextTier: this.getNextTier(reputation.score)
      };
    } catch (error) {
      console.error('Error getting reputation dashboard:', sanitizeErrorMessage(error));
      return null;
    }
  }

  /**
   * Get next reputation tier info
   */
  getNextTier(currentScore) {
    const tiers = Object.entries(this.REPUTATION_TIERS);
    for (let i = 0; i < tiers.length; i++) {
      const [tierName, tierInfo] = tiers[i];
      if (currentScore < tierInfo.min) {
        return {
          name: tierName,
          ...tierInfo,
          pointsNeeded: tierInfo.min - currentScore
        };
      }
    }
    return null; // Already at highest tier
  }

  /**
   * Rate limiting helpers to prevent 429 errors
   */
  canMakeRpcCall() {
    // Reset counter if time window has passed
    if (Date.now() > this.rpcResetTime) {
      this.rpcCallCount = 0;
      this.rpcResetTime = Date.now() + 60000; // Reset every minute
    }
    
    return this.rpcCallCount < this.rpcCallLimit;
  }

  incrementRpcCall() {
    this.rpcCallCount++;
  }

  /**
   * Get default reputation when RPC calls fail
   */
  getDefaultReputation() {
    return {
      score: 0,
      tier: 'NEWCOMER',
      tierInfo: this.REPUTATION_TIERS.NEWCOMER,
      activities: 0,
      stakedAmount: 0,
      stakingBonus: 0,
      lastUpdated: Date.now(),
      isDefault: true
    };
  }

  /**
   * Get default stake requirement when RPC calls fail
   */
  getDefaultStakeRequirement() {
    return {
      required: this.STAKING_REQUIREMENTS.POST || 100,
      tier: 'NEWCOMER',
      multiplier: 1.0,
      isDefault: true
    };
  }

  /**
   * Clear cache (useful for testing or manual refresh)
   */
  clearCache() {
    this.cache.clear();
    console.log('🧹 ReputationService cache cleared');
  }
}

export default ReputationService;
